import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { bind } from 'decko';
import { ButtonV2, ActionButton, TooltipTrigger, Icon } from 'components';
import { DurinClient } from 'lib/durin';
import { SmaugClient } from 'lib/smaug';
import { getAuthTokenFromState } from 'lib/auth';
import RewriteTooltip from './RewriteTooltip';
import './style.scss';

class TextInput extends Component {
  static propTypes = {
    id: PropTypes.string,
    className: PropTypes.string,
    type: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    placeholder: PropTypes.string,
    onChange: PropTypes.func,
    onKeyPress: PropTypes.func,
    onKeyDown: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onClick: PropTypes.func,
    tabIndex: PropTypes.number,
    disabled: PropTypes.bool,
    autoComplete: PropTypes.string,
    multiline: PropTypes.bool,
    defaultValue: PropTypes.string,
    'data-cy': PropTypes.string,
    'data-testid': PropTypes.string,
    enableTextSelection: PropTypes.bool,
    reduxState: PropTypes.object,
    currentUser: PropTypes.object,
    flags: PropTypes.object,
    largeTextArea: PropTypes.bool
  };

  static defaultProps = {
    type: 'text',
    multiline: false,
    enableTextSelection: false,
    largeTextArea: false
  };

  input = null;

  constructor(props) {
    super(props);
    this.state = {
      showTooltip: false,
      selectedText: '',
      tooltipPosition: { x: 0, y: 0 },
      isTouchingUp: false,
      originalSelectionStart: 0,
      originalSelectionEnd: 0,
      selectedTone: '',
      isSelecting: false,
      originalTextBeforeTouchUp: null,
      hasBeenTouchedUp: false,
      showServiceError: false,
      lastFailedRequest: null,
      feedbackSubmitted: false,
      traceId: null
    };

    // Initialize DurinClient if text selection is enabled AND feature flag is on
    if (props.enableTextSelection && props.flags?.['messaging-ai-poc'] && props.reduxState && props.currentUser) {
      this.durinClient = new DurinClient({
        baseUrl: process.env.DURIN_API_URL,
        authTokenProvider: () => getAuthTokenFromState(props.reduxState),
        schoolId: props.currentUser?.current_school?.id
      });
    }

    // Initialize Smaug client
    this.smaugClient = new SmaugClient({
      authTokenProvider: props.reduxState ? () => getAuthTokenFromState(props.reduxState) : undefined
    });
  }

  componentDidMount() {
    if (this.props.enableTextSelection && this.props.flags?.['messaging-ai-poc']) {
      document.addEventListener('mouseup', this.handleDocumentMouseUp);
      document.addEventListener('mousedown', this.handleDocumentMouseDown);
      document.addEventListener('scroll', this.handleScroll, true);
    }
  }

  componentWillUnmount() {
    if (this.props.enableTextSelection && this.props.flags?.['messaging-ai-poc']) {
      document.removeEventListener('mouseup', this.handleDocumentMouseUp);
      document.removeEventListener('mousedown', this.handleDocumentMouseDown);
      document.removeEventListener('scroll', this.handleScroll, true);
    }
  }

  @bind
  handleChange(e) {
    if (!this.props.onChange) {
      return;
    }

    if (this.props.type === 'number') {
      const numValue = Number(e.target.value);
      const { min, max } = this.props;

      if (Number.isNaN(numValue) || numValue < min) {
        this.props.onChange(String(min));
        return;
      }

      if (numValue > max) {
        this.props.onChange(String(max));
        return;
      }
    } else if (this.props.type === 'phone' && e.target.value) {
      this.props.onChange(e.target.value.trim().replace(/\D/g, ''));
      return;
    }

    this.props.onChange(e.target.value);
  }

  @bind
  bindInput(node) {
    this.input = node;
  }

  @bind
  focus() {
    if (this.input) {
      this.input.selectionStart = this.input.selectionEnd = this.input.value.length;
      this.input.focus();
    }
  }

  @bind
  handleMouseDown(e) {
    if (!this.props.enableTextSelection || !this.props.flags?.['messaging-ai-poc']) return;

    this.setState({ isSelecting: true });
  }

  @bind
  handleMouseUp(e) {
    if (!this.props.enableTextSelection || !this.props.flags?.['messaging-ai-poc']) return;

    setTimeout(() => {
      this.checkSelection();
      this.setState({ isSelecting: false });
    }, 10);
  }

  @bind
  handleDocumentMouseDown(e) {
    if (!this.props.enableTextSelection || !this.props.flags?.['messaging-ai-poc']) return;

    // If clicking outside the input while tooltip is shown, maintain selection
    if (this.state.showTooltip && this.input && !this.input.contains(e.target)) {
      e.preventDefault();
      setTimeout(() => {
        this.maintainSelection();
      }, 0);
    }
  }

  @bind
  handleDocumentMouseUp(e) {
    if (!this.props.enableTextSelection || !this.props.flags?.['messaging-ai-poc']) return;
    if (!this.state.isSelecting) return;

    // Check if the mouse up happened outside the input but we were selecting
    setTimeout(() => {
      this.checkSelection();
      this.setState({ isSelecting: false });
    }, 10);
  }

  @bind
  async handleTouchUpOption(tone) {
    const { selectedText, originalSelectionStart, originalSelectionEnd } = this.state;

    if (!selectedText || !this.durinClient || !this.props.onChange) {
      this.setState({ showTooltip: false, selectedText: '' });
      return;
    }

    // Store original text before touch-up if this is the first touch-up
    const currentValue = this.props.value || '';
    if (!this.state.hasBeenTouchedUp) {
      this.setState({
        originalTextBeforeTouchUp: currentValue
      });
    }

    this.setState({
      isTouchingUp: true,
      showTooltip: false,
      showServiceError: false,
      feedbackSubmitted: false
    });

    // Clear the message content immediately for non-large text areas
    if (!this.props.largeTextArea && this.props.onChange) {
      this.props.onChange('');
    }

    // TEMPORARY TEST CODE - Uncomment to simulate service unavailability after 10 seconds
    // if (Math.random() > 0.5) {
    //   setTimeout(() => {
    //     // Store the failed request details for retry
    //     this.setState({
    //       lastFailedRequest: {
    //         selectedText,
    //         originalSelectionStart,
    //         originalSelectionEnd,
    //         tone
    //       },
    //       isTouchingUp: false,
    //       showServiceError: true,
    //       selectedText: '',
    //       originalSelectionStart: 0,
    //       originalSelectionEnd: 0,
    //       selectedTone: ''
    //     });
    //   }, 10000); // Changed to 10 seconds to match real timeout behavior
    //   return;
    // }

    try {
      let accumulatedContent = '';
      let hasReceivedData = false;
      let streamStartTime = Date.now();
      let timeoutId;

      // Set up 10-second timeout
      const timeoutPromise = new Promise((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(new Error('Stream timeout after 10 seconds'));
        }, 10000);
      });

      const stream = await this.durinClient.rewriteStream(this.props.value || '', {
        tone: tone,
        target_text: selectedText
      });

      // Capture the trace ID from response headers
      const responseHeaders = this.durinClient.getLastResponseHeaders();
      const traceId = responseHeaders ? responseHeaders.get('x-procare-trace-id') : null;
      this.setState({ traceId });

      // Race between stream processing and timeout
      await Promise.race([
        (async () => {
          for await (const chunk of stream) {
            if (chunk.type === 'content' && chunk.delta) {
              if (!hasReceivedData) {
                hasReceivedData = true;
                clearTimeout(timeoutId); // Clear timeout once we start receiving data
              }
              accumulatedContent += chunk.delta;
              // Update the UI in real-time as content streams in
              this.props.onChange(accumulatedContent);
            }
          }
        })(),
        timeoutPromise
      ]);

      clearTimeout(timeoutId);
    } catch (error) {
      console.error('Touch up failed:', error);

      // Check if it's a 400 or 500 error code
      const is400Or500Error = error.status >= 400 && error.status < 600;

      // Trigger error state for 400/500 errors
      if (is400Or500Error) {
        // Store the failed request details for retry
        this.setState({
          lastFailedRequest: {
            selectedText,
            originalSelectionStart,
            originalSelectionEnd,
            tone
          },
          showServiceError: true
        });
      } else if (error.message.includes('Stream timeout after 10 seconds')) {
        // Only perform health check if the error is specifically our 10-second timeout
        try {
          // Perform health check
          const healthResponse = await this.durinClient.healthCheck();
          if (healthResponse.status !== 'ok') {
            // Store the failed request details for retry
            this.setState({
              lastFailedRequest: {
                selectedText,
                originalSelectionStart,
                originalSelectionEnd,
                tone
              },
              showServiceError: true
            });
          }
        } catch (healthError) {
          console.error('Health check failed:', healthError);
          // Store the failed request details for retry
          this.setState({
            lastFailedRequest: {
              selectedText,
              originalSelectionStart,
              originalSelectionEnd,
              tone
            },
            showServiceError: true
          });
        }
      }
    } finally {
      this.setState({
        isTouchingUp: false,
        selectedText: '',
        originalSelectionStart: 0,
        originalSelectionEnd: 0,
        selectedTone: '',
        hasBeenTouchedUp: !this.state.showServiceError
      });
    }
  }

  @bind
  hideTooltip() {
    this.setState({ showTooltip: false, selectedText: '', selectedTone: '' });
  }

  @bind
  handleInputBlur(e) {
    // Delay to allow tooltip interactions
    setTimeout(() => {
      if (this.state.showTooltip) {
        this.maintainSelection();
      }
    }, 100);

    // Call original onBlur if provided
    if (this.props.onBlur) {
      this.props.onBlur(e);
    }
  }

  @bind
  handleInputFocus(e) {
    // Restore selection if tooltip is shown
    if (this.state.showTooltip) {
      setTimeout(() => {
        this.maintainSelection();
      }, 0);
    }

    // Call original onFocus if provided
    if (this.props.onFocus) {
      this.props.onFocus(e);
    }
  }

  @bind
  handleToneSelection(tone) {
    this.setState({ selectedTone: tone });
  }

  @bind
  handleSubmit() {
    const { selectedTone } = this.state;
    if (selectedTone && selectedTone !== '') {
      this.handleTouchUpOption(selectedTone);
    }
  }

  @bind
  handleRevertToOriginal() {
    const { originalTextBeforeTouchUp } = this.state;
    if (originalTextBeforeTouchUp !== null && this.props.onChange) {
      this.props.onChange(originalTextBeforeTouchUp);
      this.setState({
        hasBeenTouchedUp: false,
        originalTextBeforeTouchUp: null,
        showServiceError: false,
        feedbackSubmitted: false
      });
    }
  }

  @bind
  dismissServiceError() {
    this.setState({ showServiceError: false });
  }

  @bind
  tryAgainRewrite() {
    const { lastFailedRequest } = this.state;
    this.setState({ showServiceError: false });
    if (lastFailedRequest) {
      // Restore the original text first
      if (this.props.onChange && this.state.originalTextBeforeTouchUp !== null) {
        this.props.onChange(this.state.originalTextBeforeTouchUp);
      }
      // Restore the failed request state and retry
      this.setState(
        {
          selectedText: lastFailedRequest.selectedText,
          originalSelectionStart: lastFailedRequest.originalSelectionStart,
          originalSelectionEnd: lastFailedRequest.originalSelectionEnd,
          selectedTone: lastFailedRequest.tone
        },
        () => {
          // Retry the request with the stored parameters
          this.handleTouchUpOption(lastFailedRequest.tone);
        }
      );
    }
  }

  @bind
  async sendFeedback(isPositive) {
    try {
      // Submit the rating if we have a trace ID
      if (this.state.traceId) {
        await this.smaugClient.submitRating({
          correlation_id: this.state.traceId,
          rating: isPositive ? 'up' : 'down',
          comments: null
        });
      }

      // Set feedback submitted state to show thank you message
      this.setState({ feedbackSubmitted: true });
    } catch (error) {
      console.error('Error submitting feedback to Smaug:', error);
      // Still set feedback submitted to hide the buttons even if the API call fails
      this.setState({ feedbackSubmitted: true });
    }
  }

  @bind
  handleKeyUp(e) {
    if (!this.props.enableTextSelection || !this.props.flags?.['messaging-ai-poc']) return;

    // Handle selection via keyboard (shift + arrows, ctrl+a, etc.)
    setTimeout(() => {
      this.checkSelection();
    }, 10);
  }

  @bind
  handleScroll() {
    if (!this.props.enableTextSelection || !this.props.flags?.['messaging-ai-poc']) return;
    if (!this.state.showTooltip) return;

    // Recalculate tooltip position when scrolling
    const { originalSelectionStart } = this.state;
    if (originalSelectionStart !== undefined) {
      const position = this.getSelectionPosition(originalSelectionStart);
      this.setState({ tooltipPosition: position });
    }
  }

  @bind
  checkSelection() {
    if (!this.input) return;

    const start = this.input.selectionStart;
    const end = this.input.selectionEnd;
    const selectedText = this.input.value.substring(start, end).trim();

    if (selectedText && start !== end) {
      const position = this.getSelectionPosition(start);

      this.setState({
        showTooltip: true,
        selectedText,
        originalSelectionStart: start,
        originalSelectionEnd: end,
        tooltipPosition: position
      });

      // Maintain the selection
      this.maintainSelection();
    } else {
      this.setState({ showTooltip: false, selectedText: '' });
    }
  }

  @bind
  maintainSelection() {
    if (!this.input || !this.state.showTooltip) return;

    // Restore selection if it was lost
    const currentStart = this.input.selectionStart;
    const currentEnd = this.input.selectionEnd;
    const { originalSelectionStart, originalSelectionEnd } = this.state;

    if (currentStart !== originalSelectionStart || currentEnd !== originalSelectionEnd) {
      requestAnimationFrame(() => {
        if (this.input && this.state.showTooltip) {
          this.input.setSelectionRange(originalSelectionStart, originalSelectionEnd);
        }
      });
    }
  }

  @bind
  getSelectionPosition(selectionStart) {
    if (!this.input) return { x: 0, y: 0 };

    const inputRect = this.input.getBoundingClientRect();
    const style = window.getComputedStyle(this.input);
    const paddingLeft = parseFloat(style.paddingLeft || 0);
    const paddingTop = parseFloat(style.paddingTop || 0);
    const borderLeft = parseFloat(style.borderLeftWidth || 0);
    const borderTop = parseFloat(style.borderTopWidth || 0);

    if (this.props.multiline) {
      // For textarea, calculate line and character position
      const textBeforeSelection = this.input.value.substring(0, selectionStart);
      const lines = textBeforeSelection.split('\n');
      const currentLine = lines.length - 1;
      const currentLineText = lines[lines.length - 1];

      // Get text measurements
      const fontSize = parseFloat(style.fontSize);
      const lineHeight = parseFloat(style.lineHeight) || fontSize * 1.2;
      const textWidth = this.measureTextWidth(currentLineText, style);

      const x = inputRect.left + paddingLeft + borderLeft + textWidth;
      // Position tooltip above the specific line where selection starts
      const lineY = inputRect.top + paddingTop + borderTop + currentLine * lineHeight;
      const y = lineY - 50;

      return { x, y };
    } else {
      // For input, measure horizontal position
      const textBeforeSelection = this.input.value.substring(0, selectionStart);
      const textWidth = this.measureTextWidth(textBeforeSelection, style);

      const x = inputRect.left + paddingLeft + borderLeft + textWidth;
      const y = inputRect.top - 50;

      return { x, y };
    }
  }

  @bind
  measureTextWidth(text, style) {
    // Create a temporary canvas to measure text width accurately
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    // Apply the same font properties as the input
    const fontSize = style.fontSize;
    const fontFamily = style.fontFamily;
    const fontWeight = style.fontWeight;
    const fontStyle = style.fontStyle;

    context.font = `${fontStyle} ${fontWeight} ${fontSize} ${fontFamily}`;

    return context.measureText(text).width;
  }

  render() {
    const { props } = this;
    const {
      isTouchingUp,
      selectedTone,
      hasBeenTouchedUp,
      originalTextBeforeTouchUp,
      showServiceError,
      feedbackSubmitted
    } = this.state;

    const inputCN = classNames({
      input: true,
      'input--textarea': props.multiline,
      [props.className]: Boolean(props.className)
    });

    const textInputProps = {
      className: inputCN,
      id: props.name,
      autoFocus: props.autoFocus,
      value: props.value,
      defaultValue: props.defaultValue,
      placeholder: props.placeholder,
      onChange: this.handleChange,
      onFocus: this.handleInputFocus,
      onBlur: this.handleInputBlur,
      disabled: props.disabled,
      onKeyPress: props.onKeyPress,
      onKeyDown: props.onKeyDown,
      onKeyUp: props.enableTextSelection && props.flags?.['messaging-ai-poc'] ? this.handleKeyUp : undefined,
      onClick: props.onClick,
      onMouseDown: props.enableTextSelection && props.flags?.['messaging-ai-poc'] ? this.handleMouseDown : undefined,
      onMouseUp: props.enableTextSelection && props.flags?.['messaging-ai-poc'] ? this.handleMouseUp : undefined,
      autoComplete: props.autoComplete,
      tabIndex: props.tabIndex,
      ref: this.bindInput,
      min: props.min,
      max: props.max,
      'data-cy': props['data-cy'],
      'data-testid': props['data-testid']
    };

    // only enable numeric inputs
    if (props.type === 'number') {
      if (props.decimal) {
        textInputProps.step = 0.01;
        textInputProps.pattern = 'd+((.)d{1,2})?';
      } else {
        textInputProps.pattern = '[0-9]*';
      }
    }

    const inputElement = props.multiline ? (
      <textarea {...textInputProps} />
    ) : (
      <input type={props.type} {...textInputProps} />
    );

    return (
      <div>
        {props.enableTextSelection && props.flags?.['messaging-ai-poc'] && (
          <div
            className={classNames('text-input__revert-container', {
              'text-input__revert-container--space-between': hasBeenTouchedUp && originalTextBeforeTouchUp !== null,
              'text-input__revert-container--start': !(hasBeenTouchedUp && originalTextBeforeTouchUp !== null)
            })}
          >
            <div className="ai-content">
              <TooltipTrigger
                className="procare-ai-icon"
                side="left"
                tooltip={hasBeenTouchedUp ? 'Written with the help of Procare AI ' : 'Rewrite with Procare AI'}
              >
                <Icon name="ai" size={props.largeTextArea ? 20 : 16} />
              </TooltipTrigger>
              {hasBeenTouchedUp && originalTextBeforeTouchUp !== null && (
                <div className="text-input__feedback">
                  {feedbackSubmitted ? (
                    <span className="text-input__feedback-message">Thank you for the feedback!</span>
                  ) : (
                    <>
                      <span className="text-input__feedback-message">Is this rewrite what you were looking for?</span>
                      <div className="text-input__feedback-buttons">
                        <ActionButton
                          icon
                          iconName="thumbs-up"
                          onClick={() => this.sendFeedback(true)}
                          ariaLabel="Thumbs up"
                        />
                        <ActionButton
                          icon
                          iconName="thumbs-down"
                          onClick={() => this.sendFeedback(false)}
                          ariaLabel="Thumbs down"
                        />
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
            {hasBeenTouchedUp && originalTextBeforeTouchUp !== null && (
              <ButtonV2
                size="small"
                tertiary
                label="Revert to Original"
                onClick={this.handleRevertToOriginal}
                iconName="undo"
              />
            )}
          </div>
        )}
        <div className="text-input__container">
          {inputElement}
          {isTouchingUp && props.enableTextSelection && props.flags?.['messaging-ai-poc'] && props.largeTextArea && (
            <div className="text-input__loading-overlay">
              <img src="/assets/images/placeholders/loading.gif" alt="Loading..." />
            </div>
          )}
          {showServiceError && props.enableTextSelection && props.flags?.['messaging-ai-poc'] && props.largeTextArea && (
            <div className="text-input__error-overlay">
              <img src="/assets/images/placeholders/no-result/yoga-tucker.png" alt="Service Unavailable" />
              <div className="error-content">
                <div className="error-title">Service Temporarily Unavailable</div>
                <div className="error-message">Sorry, our Rewrite Service was temporarily unavailable.</div>
              </div>
              <div className="error-buttons">
                <ButtonV2
                  size="small"
                  secondary
                  label="Show Original"
                  onClick={this.handleRevertToOriginal}
                  iconName="undo"
                />
                <ButtonV2 size="small" primary label="Try Again" onClick={this.tryAgainRewrite} iconName="refresh" />
              </div>
            </div>
          )}
          {/* Default loading state for non-large text areas */}
          {isTouchingUp && props.enableTextSelection && props.flags?.['messaging-ai-poc'] && !props.largeTextArea && (
            <div className="text-input__loading-small">
              <img src="/assets/images/placeholders/loading.gif" alt="Loading..." />
            </div>
          )}
          {this.state.showTooltip && props.enableTextSelection && props.flags?.['messaging-ai-poc'] && (
            <RewriteTooltip
              position={this.state.tooltipPosition}
              selectedTone={selectedTone}
              isTouchingUp={isTouchingUp}
              onToneSelection={this.handleToneSelection}
              onSubmit={this.handleSubmit}
              onClose={this.hideTooltip}
            />
          )}
        </div>
        {/* Default error state for non-large text areas */}
        {showServiceError && props.enableTextSelection && props.flags?.['messaging-ai-poc'] && !props.largeTextArea && (
          <div className="text-input__error-message">
            <span>Service temporarily unavailable. Sorry, our Rewrite Service was temporarily unavailable.</span>
          </div>
        )}
      </div>
    );
  }
}

export default TextInput;
